<template>
  <div class="user-profile-activity-page">
    <b-alert v-if="loadError" show variant="danger" class="mb-2">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchUserProfile">Retry</b-button>
    </b-alert>
    <div v-if="isLoading" class="text-center py-2 mb-2">
      <b-spinner label="Loading user profile..." />
    </div>
    <b-row v-else>
      <b-col lg="4" md="12" class="mb-2 mb-lg-0">
        <b-card class="h-100 left-card">
          <div class="text-center mb-1">
            <b-img
              fluid
              :src="profileBanner"
              class="profile-banner"
              alt=""
            />
          </div>
          <small class="text-muted d-block text-center">User Details</small>

          <div class="details mt-1">
            <p><strong>Name:</strong> {{ user.name }}</p>
            <p><strong>Email:</strong> {{ user.email }}</p>
            <p><strong>Phone:</strong> {{ user.phone }}</p>
            <p><strong>DOB:</strong> {{ user.dob }}</p>
            <p><strong>Password :</strong> {{ user.passwordMask }}</p>
            <p><strong>Selected category:</strong> {{ user.category }}</p>
            <p><strong>Joining date:</strong> {{ user.joined }}</p>
          </div>

          <div class="d-flex justify-content-between mt-1">
            <b-button variant="primary" class="action-btn action-btn--primary mb-50" @click="openUserEdit">
              Edit User
            </b-button>
            <div>
              <b-button variant="danger" class="action-btn mr-50" @click="blockUser">Block User</b-button>
              <b-button variant="danger" class="action-btn" @click="suspendUser">Suspend</b-button>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col lg="8" md="12">
        <b-card class="h-100 right-card">
          <b-tabs v-model="activeTabIndex" pills nav-class="mb-2 activity-tabs" nav-wrapper-class="activity-tabs-wrap">
            <b-tab title="Your Activity">
              <b-row class="mb-1 stat-row">
                <b-col lg="4" md="12" class="mb-1 mb-lg-0">
                  <b-card no-body class="stat-card stat-card-bar h-100">
                    <b-card-body class="p-1 stat-card-body">
                      <div class="stat-split">
                        <div class="stat-text-block">
                          <div class="stat-title-navy">Time Spent on Ads</div>
                          <div class="stat-metric-navy">{{ activitySummary.timeSpentLabel }}</div>
                          <small class="stat-daily-muted">Daily average</small>
                        </div>
                        <div class="stat-chart-wrap stat-chart-wrap--bar">
                          <vue-apex-charts
                            :key="`bar-chart-${activeTabIndex}`"
                            type="bar"
                            height="110"
                            :options="barOptions"
                            :series="barSeries"
                          />
                        </div>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
                <b-col lg="4" md="12" class="mb-1 mb-lg-0">
                  <b-card no-body class="stat-card stat-card-line h-100">
                    <b-card-body class="p-1 stat-card-body">
                      <div class="stat-split">
                        <div class="stat-text-block">
                          <div class="stat-title-navy">Total Ads Viewed</div>
                          <div class="stat-metric-navy">{{ formatCompactNumber(activitySummary.totalAdsViewed) }}</div>
                        </div>
                        <div class="stat-chart-wrap stat-chart-wrap--line">
                          <vue-apex-charts
                            :key="`line-chart-${activeTabIndex}`"
                            type="line"
                            height="110"
                            :options="lineOptions"
                            :series="lineSeries"
                          />
                        </div>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
                <b-col lg="4" md="12">
                  <div class="stat-card stat-card-clicks h-100 d-flex flex-column justify-content-center">
                    <div class="stat-clicks-inner">
                      <div class="stat-clicks-title">Total Ad Clicks</div>
                      <div class="stat-clicks-value">{{ formatCompactNumber(activitySummary.totalAdClicks) }}</div>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <b-table
                :items="activityRows"
                :fields="activityFields"
                responsive
                class="activity-table mb-0"
                tbody-tr-class="activity-row"
              >
                <template #cell(ads)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar :src="data.item.adThumb" size="34" class="mr-50 rounded-sm" />
                    <span>{{ data.item.adTitle }}</span>
                  </div>
                </template>
                <template #cell(company)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar :src="data.item.companyLogo" size="28" class="mr-50" />
                    <span>{{ data.item.companyName }}</span>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="MailIcon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="contactUser(data.item)"
                    />
                    <feather-icon
                      icon="Trash2Icon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="removeActivityRow(data.item)"
                    />
                  </div>
                </template>
              </b-table>

              <AdminPaginationBar
                :page="activityPage"
                :per-page="activityPagination.perPage"
                :total="activityPagination.total"
                @change="onActivityPageChange"
              />
            </b-tab>

            <b-tab title="Saved Ads">
              <b-row>
                <b-col
                  v-for="(ad, i) in savedAdCards"
                  :key="`saved-${i}`"
                  lg="4"
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <b-card no-body class="saved-ad-card h-100">
                    <div class="saved-ad-image-wrap">
                      <b-img :src="ad.image" fluid class="saved-ad-image" />
                      <span class="saved-ad-badge mypoda-status-tag" :class="ad.status === 'Active' ? 'status-active' : 'status-inactive'">
                        {{ ad.status }}
                      </span>
                      <b-avatar size="28" :src="ad.companyLogo" class="saved-ad-logo-float" />
                    </div>
                    <div class="saved-ad-footer">
                      <h5 class="saved-ad-title mb-50">{{ ad.title }}</h5>
                      <small class="saved-ad-stats d-block">
                        {{ formatCompactNumber(ad.likes) }} Likes ·
                        {{ formatCompactNumber(ad.dislikes) }} Dislike ·
                        {{ formatCompactNumber(ad.reports) }} Reports
                      </small>
                      <small class="saved-ad-stats d-block mt-25">
                        {{ formatCompactNumber(ad.views) }} views ·
                        {{ formatCompactNumber(ad.shares) }} Share ·
                        {{ formatCompactNumber(ad.savedCount) }} Saved
                      </small>
                      <b-button
                        size="sm"
                        variant="light"
                        class="saved-bookmark-btn"
                        @click="toggleSavedAd(i)"
                      >
                        <feather-icon icon="BookmarkIcon" size="16" class="text-dark" />
                      </b-button>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
            </b-tab>

            <b-tab title="Reported Ads">
              <b-table
                :items="reportedRows"
                :fields="reportedFields"
                responsive
                class="reported-table mb-0"
                thead-class="table-head-muted"
                tbody-tr-class="reported-row"
              >
                <template #cell(reportId)="data">
                  <span>{{ String(data.item.reportId).padStart(2, '0') }}</span>
                </template>
                <template #cell(ads)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar :src="data.item.adLogo" size="32" class="mr-1 rounded-sm" />
                    <span>{{ data.item.adTitle }}</span>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="MailIcon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="contactUser(data.item)"
                    />
                    <feather-icon
                      icon="Trash2Icon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="removeReportedRow(data.item)"
                    />
                  </div>
                </template>
              </b-table>
              <AdminPaginationBar
                :page="reportedPage"
                :per-page="reportedPagination.perPage"
                :total="reportedPagination.total"
                @change="onReportedPageChange"
              />
            </b-tab>

            <b-tab title="Followed Brands">
              <b-table
                :items="followedRows"
                :fields="followedFields"
                responsive
                class="followed-table mb-0"
                thead-class="table-head-muted"
                tbody-tr-class="followed-row"
              >
                <template #cell(id)="data">
                  <span>{{ String(data.item.id).padStart(2, '0') }}</span>
                </template>
                <template #cell(companyName)="data">
                  <div class="d-flex align-items-center">
                    <b-avatar size="34" :src="data.item.logo" class="mr-1" />
                    <span>{{ data.item.companyName }}</span>
                  </div>
                </template>
                <template #cell(followers)="data">
                  <b-avatar-group size="26">
                    <b-avatar v-for="(av, j) in data.item.followerAvatars" :key="j" :src="av" />
                    <b-avatar variant="light-secondary" :text="`+${data.item.extraFollowers}`" />
                  </b-avatar-group>
                </template>
                <template #cell(actions)="data">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="MailIcon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="contactUser(data.item)"
                    />
                    <feather-icon
                      icon="Trash2Icon"
                      size="15"
                      class="mx-50 text-muted cursor-pointer"
                      @click="unfollowCompany(data.item)"
                    />
                  </div>
                </template>
              </b-table>
              <AdminPaginationBar
                :page="followedPage"
                :per-page="followedPagination.perPage"
                :total="followedPagination.total"
                @change="onFollowedPageChange"
              />
            </b-tab>

            <b-tab title="Content Preferences">
              <div v-if="preferencesLoading" class="text-center py-2">
                <b-spinner small label="Loading preferences..." />
              </div>
              <template v-else>
              <div class="d-flex flex-wrap mb-1 pref-toolbar">
                <b-button variant="outline-secondary" class="pref-action-btn mr-1" @click="resetPreferences">
                  Reset Preferences
                </b-button>
                <b-button variant="outline-secondary" class="pref-action-btn" @click="updatePreferences">
                  Update Preferences
                </b-button>
              </div>
              <b-row class="pref-grid">
                <b-col
                  v-for="cat in preferenceCategories"
                  :key="cat.id"
                  cols="6"
                  sm="4"
                  md="4"
                  lg="3"
                  xl="2"
                  class="mb-1 pref-col"
                >
                  <div
                    class="pref-card text-center h-100"
                    :class="{ 'pref-card--selected': selectedPreferenceId === cat.id }"
                    role="button"
                    tabindex="0"
                    @click="selectedPreferenceId = cat.id"
                    @keydown.enter.prevent="selectedPreferenceId = cat.id"
                  >
                    <div class="pref-icon-wrap">
                      <feather-icon :icon="cat.icon" size="28" class="pref-icon" />
                      <span v-if="selectedPreferenceId === cat.id" class="pref-check">
                        <feather-icon icon="CheckIcon" size="12" />
                      </span>
                    </div>
                    <div class="pref-label">{{ cat.title }}</div>
                  </div>
                </b-col>
              </b-row>
              <p v-if="!preferenceCategories.length" class="text-muted text-center py-2 mb-0">
                No categories available.
              </p>
              </template>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BAvatarGroup,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BImg,
  BRow,
  BSpinner,
  BTable,
  BTab,
  BTabs,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { formatAdminDate, normalizePagination, resolveMediaUrl, unwrapAdminResponse } from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifyInfo, notifySuccess } from '@/utils/uiActions'

const TAB_LABELS = ['Your Activity', 'Saved Ads', 'Reported Ads', 'Followed Brands', 'Content Preferences']

export default {
  name: 'UserProfileActivity',
  components: {
    BAlert,
    BAvatar,
    BAvatarGroup,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BImg,
    BRow,
    BSpinner,
    BTable,
    BTab,
    BTabs,
    VueApexCharts,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      activeTabIndex: 0,
      profileBanner: '',
      user: {
        name: '',
        email: '',
        phone: '',
        dob: '',
        passwordMask: '**********',
        category: '',
        joined: '',
      },
      activityPage: 1,
      activitySummary: {
        timeSpentLabel: '0m',
        totalAdsViewed: 0,
        totalAdClicks: 0,
      },
      activityPagination: {
        total: 0,
        perPage: 8,
      },
      activityFields: [
        { key: 'date', label: 'DATE', tdClass: 'col-date', thClass: 'col-date' },
        { key: 'ads', label: 'ADS' },
        { key: 'activityType', label: 'ACTIVITY TYPE' },
        { key: 'company', label: 'COMPANY' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      activityRows: [],
      savedAdCards: [],
      reportedPage: 1,
      reportedPagination: {
        total: 0,
        perPage: 10,
      },
      reportedFields: [
        { key: 'reportId', label: 'REPORT ID' },
        { key: 'ads', label: 'ADS' },
        { key: 'report', label: 'REPORT' },
        { key: 'date', label: 'DATE' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      reportedRows: [],
      followedPage: 1,
      followedPagination: {
        total: 0,
        perPage: 8,
      },
      followedFields: [
        { key: 'id', label: 'ID' },
        { key: 'companyName', label: 'BRAND' },
        { key: 'followers', label: 'FOLLOWERS' },
        { key: 'followDate', label: 'FOLLOW DATE', tdClass: 'col-date', thClass: 'col-date' },
        { key: 'adsPosted', label: 'ADS POSTED' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      followedRows: [],
      selectedPreferenceId: null,
      initialPreferenceId: null,
      preferenceCategories: [],
      preferencesLoading: false,
      barSeries: [{ name: 'Minutes', data: [10, 12, 9, 14, 24, 11, 10] }],
      barOptions: {
        chart: {
          toolbar: { show: false },
          sparkline: { enabled: false },
          animations: { enabled: true, speed: 400 },
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            columnWidth: '52%',
            distributed: true,
          },
        },
        colors: ['#c9c2f0', '#c9c2f0', '#c9c2f0', '#c9c2f0', '#4a7ff0', '#c9c2f0', '#c9c2f0'],
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
          labels: { style: { colors: '#9ca3af', fontSize: '11px', fontWeight: 500 } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { show: false },
        grid: { show: false, padding: { top: 4, right: 0, bottom: 0, left: 0 } },
      },
      lineSeries: [{ name: 'Views', data: [1500, 2100, 2600, 2400, 3000, 3600, 5000] }],
      lineOptions: {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, speed: 400 },
        },
        stroke: { width: 2.5, curve: 'smooth' },
        colors: ['#4a7ff0'],
        dataLabels: { enabled: false },
        markers: {
          size: 0,
          hover: { size: 0 },
          discrete: [
            {
              seriesIndex: 0,
              dataPointIndex: 6,
              fillColor: '#4a7ff0',
              strokeColor: '#fff',
              strokeWidth: 2,
              size: 7,
            },
          ],
        },
        xaxis: {
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          categories: ['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'],
        },
        yaxis: { show: false },
        grid: {
          borderColor: '#e8e8f0',
          strokeDashArray: 4,
          padding: { top: 8, right: 4, bottom: 0, left: 4 },
        },
        tooltip: { enabled: false },
        annotations: {
          xaxis: [
            {
              x: 'Su',
              borderColor: '#4a7ff0',
              strokeDashArray: 5,
              borderWidth: 1,
              opacity: 0.9,
              label: { show: false },
            },
          ],
        },
      },
    }
  },
  computed: {
    userId() {
      return this.$route.params.id
    },
  },
  watch: {
    userId: {
      immediate: false,
      handler(id) {
        if (!id) return
        this.activeTabIndex = 0
        this.activityPage = 1
        this.reportedPage = 1
        this.followedPage = 1
        this.fetchUserProfile()
        this.fetchUserActivity()
        this.fetchUserReports()
      },
    },
    activeTabIndex() {
      this.syncRouteMeta()
      if (this.activeTabIndex === 0) {
        this.fetchUserActivity()
      }
      if (this.activeTabIndex === 1) {
        this.fetchSavedAds()
      }
      if (this.activeTabIndex === 3) {
        this.fetchFollowedCompanies()
      }
      if (this.activeTabIndex === 2) {
        this.fetchUserReports()
      }
      if (this.activeTabIndex === 4) {
        this.fetchContentPreferences()
      }
    },
  },
  mounted() {
    this.syncRouteMeta()
    this.fetchUserProfile()
    this.fetchUserActivity()
    this.fetchUserReports()
  },
  methods: {
    onActivityPageChange(page) {
      this.activityPage = page
      this.fetchUserActivity()
    },
    onReportedPageChange(page) {
      this.reportedPage = page
      this.fetchUserReports()
    },
    onFollowedPageChange(page) {
      this.followedPage = page
      this.fetchFollowedCompanies()
    },
    formatCompactNumber(value) {
      const num = Number(value) || 0
      if (num >= 1000000) {
        return `${Math.round(num / 100000) / 10}M`.replace('.0M', 'M')
      }
      if (num >= 1000) {
        return `${Math.round(num / 100) / 10}K`.replace('.0K', 'K')
      }
      return String(num)
    },
    applyActivityCharts(charts) {
      const categories = charts?.time_spent?.categories || charts?.ads_viewed?.categories || []
      const barData = charts?.time_spent?.data || []
      const lineData = charts?.ads_viewed?.data || []
      this.barSeries = [{ name: 'Minutes', data: barData.length ? barData : [0, 0, 0, 0, 0, 0, 0] }]
      this.lineSeries = [{ name: 'Views', data: lineData.length ? lineData : [0, 0, 0, 0, 0, 0, 0] }]
      const dayLabels = categories.length ? categories : ['M', 'T', 'W', 'T', 'F', 'S', 'S']
      this.barOptions = {
        ...this.barOptions,
        xaxis: { ...this.barOptions.xaxis, categories: dayLabels },
      }
      this.lineOptions = {
        ...this.lineOptions,
        xaxis: { ...this.lineOptions.xaxis, categories: dayLabels },
      }
    },
    async fetchUserActivity() {
      if (!this.userId) return
      try {
        const res = await adminPanelApi.userActivity(this.userId, {
          page: this.activityPage,
          per_page: this.activityPagination.perPage,
        })
        const payload = unwrapAdminResponse(res)
        const summary = payload.summary || {}
        this.activitySummary = {
          timeSpentLabel: summary.time_spent_label || '0m',
          totalAdsViewed: summary.total_ads_viewed ?? 0,
          totalAdClicks: summary.total_ad_clicks ?? 0,
        }
        this.applyActivityCharts(payload.charts || {})
        const list = payload.activity || []
        this.activityRows = list.map(row => ({
          date: row.date || '',
          adTitle: row.ad_title || 'Ad',
          adThumb: resolveMediaUrl(row.ad_image),
          activityType: row.activity_type || '',
          companyName: row.company_name || '',
          companyLogo: resolveMediaUrl(row.company_logo),
        }))
        const pagination = normalizePagination(payload, {}, res)
        this.activityPagination = {
          total: pagination.total || list.length,
          perPage: pagination.perPage || this.activityPagination.perPage,
        }
        this.activityPage = pagination.currentPage || this.activityPage
      } catch (e) {
        this.activityRows = []
        this.activityPagination = { total: 0, perPage: 8 }
      }
    },
    async fetchSavedAds() {
      if (!this.userId) return
      try {
        const res = await adminPanelApi.userSavedAds(this.userId, { page: 1, per_page: 12 })
        const payload = unwrapAdminResponse(res)
        const list = payload.saved_ads || []
        this.savedAdCards = list.map(ad => ({
          id: ad.id,
          title: ad.title || 'Ad',
          image: resolveMediaUrl(ad.image),
          companyLogo: resolveMediaUrl(ad.company_logo),
          status: ad.status || 'Active',
          likes: ad.likes ?? 0,
          dislikes: ad.dislikes ?? 0,
          reports: ad.reports ?? 0,
          views: ad.views ?? 0,
          shares: ad.shares ?? 0,
          savedCount: ad.saved_count ?? 0,
        }))
      } catch (e) {
        this.savedAdCards = []
      }
    },
    async fetchUserProfile() {
      if (!this.userId) return
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.userDetails(this.userId)
        const profile = unwrapAdminResponse(res)
        this.user = {
          name: profile.username || profile.name || '',
          email: profile.email || '',
          phone: profile.contact || profile.phone || '',
          dob: profile.age || profile.dob || '',
          passwordMask: profile.password || '**********',
          category: profile.selected_category || '',
          joined: formatAdminDate(profile.joining_date || profile.joined_date || ''),
        }
        this.profileBanner = resolveMediaUrl(profile.profile_image)
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load user profile.'
      } finally {
        this.isLoading = false
      }
    },
    async fetchUserReports() {
      if (!this.userId) return
      try {
        const res = await adminPanelApi.userReports(this.userId, {
          page: this.reportedPage,
          per_page: this.reportedPagination.perPage,
        })
        const payload = unwrapAdminResponse(res)
        const list = payload.reports || payload.items || []
        const pagination = normalizePagination(payload, {}, res)
        this.reportedPagination = {
          total: pagination.total || list.length,
          perPage: pagination.perPage || this.reportedPagination.perPage,
        }
        this.reportedPage = pagination.currentPage || this.reportedPage
        this.reportedRows = list.map((r, i) => ({
          reportId: r.id || i + 1,
          adTitle: r.ad_title || r.ad?.title || 'Ad',
          adLogo: resolveMediaUrl(r.ad_image || r.ad?.image),
          report: r.reason || r.report || '',
          date: r.date || r.created_at || '',
          reportedByAvatar: resolveMediaUrl(r.reported_by?.profile_image),
        }))
      } catch (e) {
        this.reportedRows = []
      }
    },
    async fetchFollowedCompanies() {
      if (!this.userId) return
      try {
        const res = await adminPanelApi.followedCompanies(this.userId, {
          page: this.followedPage,
          per_page: this.followedPagination.perPage,
        })
        const payload = unwrapAdminResponse(res)
        const list = payload.companies || []
        const pagination = normalizePagination(payload, {}, res)
        this.followedPagination = {
          total: pagination.total || list.length,
          perPage: pagination.perPage || this.followedPagination.perPage,
        }
        this.followedPage = pagination.currentPage || this.followedPage
        this.followedRows = list.map(c => {
          const previews = (c.followers?.list || []).map(f => resolveMediaUrl(f.profile_image))
          const remaining = Number(c.followers?.remaining_count || 0)
          return {
            id: c.id,
            companyName: c.company_name,
            logo: resolveMediaUrl(c.logo),
            followerAvatars: previews,
            extraFollowers: remaining,
            followDate: c.follow_date,
            adsPosted: c.ads_posted ?? 0,
          }
        })
      } catch (e) {
        this.followedRows = []
      }
    },
    syncRouteMeta() {
      const label = TAB_LABELS[this.activeTabIndex] || TAB_LABELS[0]
      const meta = this.$route.meta
      if (!meta || !Array.isArray(meta.breadcrumb)) return
      meta.breadcrumb.forEach(c => {
        c.active = false
      })
      const profileCrumb = meta.breadcrumb.find(c => c.text === 'Profile')
      if (profileCrumb) {
        profileCrumb.to = { name: 'client-profile', params: { id: String(this.userId) } }
      }
      const last = meta.breadcrumb[meta.breadcrumb.length - 1]
      if (last) {
        last.text = label
        last.active = true
        delete last.to
      }
      meta.pageTitle = label
    },
    async fetchContentPreferences() {
      if (!this.userId) return
      this.preferencesLoading = true
      try {
        const res = await adminPanelApi.userContentPreferences(this.userId)
        const payload = unwrapAdminResponse(res)
        const list = payload.categories || []
        this.preferenceCategories = list.map(c => ({
          id: c.id,
          title: c.name,
          icon: 'TagIcon',
        }))
        const selected = list.find(c => c.is_selected)
        this.selectedPreferenceId = selected ? selected.id : (list[0]?.id ?? null)
        this.initialPreferenceId = this.selectedPreferenceId
      } catch {
        this.preferenceCategories = []
      } finally {
        this.preferencesLoading = false
      }
    },
    async resetPreferences() {
      if (!this.userId) return
      try {
        await adminPanelApi.resetUserContentPreferences(this.userId)
        await this.fetchContentPreferences()
        notifySuccess(this, 'Content preferences reset successfully.')
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to reset preferences.')
      }
    },
    async updatePreferences() {
      if (!this.userId || this.selectedPreferenceId == null) {
        notifyError(this, 'Select a category first.')
        return
      }
      try {
        await adminPanelApi.updateUserContentPreferences(this.userId, [this.selectedPreferenceId])
        this.initialPreferenceId = this.selectedPreferenceId
        notifySuccess(this, 'Content preferences updated.')
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to update preferences.')
      }
    },
    openUserEdit() {
      if (!this.userId) return
      this.$router.push({ name: 'client-edit', params: { id: String(this.userId) } })
    },
    blockUser() {
      confirmAction(
        this,
        { title: 'Block user?', text: `Block ${this.user.name}?`, confirmButtonText: 'Block' },
        async () => {
          try {
            await adminPanelApi.blockUser(this.userId)
            notifySuccess(this, `${this.user.name} has been blocked.`)
            await this.fetchUserProfile()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to block user.')
          }
        },
      )
    },
    suspendUser() {
      confirmAction(
        this,
        { title: 'Suspend user?', text: `Suspend ${this.user.name}?`, confirmButtonText: 'Suspend' },
        async () => {
          try {
            await adminPanelApi.suspendUser(this.userId)
            notifySuccess(this, `${this.user.name} has been suspended.`)
            await this.fetchUserProfile()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to suspend user.')
          }
        },
      )
    },
    contactUser(item) {
      const email = this.user.email || 'the user'
      notifyInfo(this, `Contact options for ${item.companyName || email} will open in your mail client when email is configured.`, 'Contact')
    },
    removeActivityRow(item) {
      this.activityRows = this.activityRows.filter(row => row !== item)
      notifyInfo(this, 'Activity entry removed from the list.', 'Removed')
    },
    removeReportedRow(item) {
      this.reportedRows = this.reportedRows.filter(row => row !== item)
      notifyInfo(this, 'Report entry removed from the list.', 'Removed')
    },
    toggleSavedAd(index) {
      this.savedAdCards.splice(index, 1)
      notifyInfo(this, 'Ad removed from saved list.', 'Saved ads')
    },
    goToFollowedCompanies() {
      const userId = this.$route.params.id
      if (userId) {
        this.$router.push({ name: 'user-followed-companies', params: { id: userId } })
        return
      }
      notifyInfo(this, 'Open this profile from User Management to view followed companies.')
    },
    unfollowCompany(item) {
      confirmAction(
        this,
        { title: 'Unfollow brand?', text: `Stop following ${item.companyName}?`, confirmButtonText: 'Unfollow' },
        () => {
          this.followedRows = this.followedRows.filter(row => row.id !== item.id)
          notifySuccess(this, `Unfollowed ${item.companyName}.`)
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.left-card,
.right-card {
  border: 1px solid #ebe9f1;
}

.profile-banner {
  max-height: 120px;
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.details p {
  margin-bottom: 0.48rem;
  color: #5e5873;
  font-size: 0.92rem;
}

.action-btn--primary {
  background: #0b1f39 !important;
  border-color: #0b1f39 !important;
  color: #fff !important;
}

.action-btn {
  background: #fbe4e6 !important;
  border-color: #fbe4e6 !important;
  color: #d66064 !important;
  flex: 1;
  margin: 0 0.25rem;
}

.stat-row {
  align-items: stretch;
}

.stat-card {
  border-radius: 12px;
  overflow: hidden;
}

.stat-card-bar,
.stat-card-line {
  border: 1px solid #f0eff5;
  background: #fff;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
}

.stat-card-body {
  min-height: 132px;
}

.stat-split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  min-height: 118px;
}

.stat-text-block {
  flex: 0 0 44%;
  max-width: 48%;
  padding-right: 0.15rem;
}

.stat-title-navy {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e3a5f;
  line-height: 1.25;
  margin-bottom: 0.35rem;
}

.stat-metric-navy {
  font-size: 1.65rem;
  font-weight: 800;
  color: #1e3a5f;
  line-height: 1.05;
  letter-spacing: -0.02em;
}

.stat-daily-muted {
  display: block;
  margin-top: 0.32rem;
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 500;
}

.stat-chart-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.stat-chart-wrap--bar {
  margin: -6px -10px -4px 0;
}

.stat-chart-wrap--line {
  margin: -4px -8px 0 0;
}

.stat-card-clicks {
  border: none;
  background: #6c72e0;
  border-radius: 12px;
  min-height: 132px;
  box-shadow: 0 12px 32px rgba(108, 114, 224, 0.48);
}

.stat-clicks-inner {
  padding: 1rem 1.1rem;
}

.stat-clicks-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.35rem;
}

.stat-clicks-value {
  font-size: 2rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.05;
  letter-spacing: -0.02em;
}

::v-deep .activity-tabs-wrap {
  background: #f4f5f8;
  padding: 0.4rem 0.45rem;
  border-radius: 10px;
  margin-bottom: 0.65rem;
}

::v-deep .activity-tabs-wrap .nav {
  flex-wrap: wrap;
  row-gap: 0.35rem;
  background: transparent !important;
  padding: 0;
  margin: 0;
}

::v-deep .activity-tabs.nav-pills .nav-link {
  padding: 0.5rem 0.9rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #a0aec0 !important;
  border-radius: 6px !important;
  border: none !important;
  background: transparent !important;
}

::v-deep .activity-tabs.nav-pills .nav-link:hover {
  color: #718096 !important;
  background: rgba(255, 255, 255, 0.55) !important;
}

/* BV / theme sets .nav-pills .nav-link.active to $primary — override (see also unscoped block below) */
::v-deep .activity-tabs-wrap .activity-tabs.nav-pills .nav-link.active {
  background-color: #041122 !important;
  border-color: #041122 !important;
  color: #fff !important;
  box-shadow: none !important;
}

::v-deep .activity-table th {
  font-size: 0.75rem;
  color: #6e6b7b;
  letter-spacing: 0.03rem;
}

::v-deep .activity-row td {
  vertical-align: middle;
}

/* Saved Ads */
.saved-ad-card {
  border-radius: 10px;
  overflow: hidden;
  border: none;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.saved-ad-image-wrap {
  position: relative;
  background: #c45c2a;
}

.saved-ad-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.saved-ad-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
}

.saved-ad-logo-float {
  position: absolute;
  bottom: 12px;
  left: 12px;
  border: 2px solid #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

::v-deep .activity-table td.col-date,
::v-deep .followed-table td.col-date,
::v-deep .reported-table td.col-date {
  white-space: nowrap;
  min-width: 110px;
}

.pref-grid {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 0.5rem;
  margin-left: 0;
  margin-right: 0;
}

.pref-col {
  flex: 0 0 150px;
  max-width: 150px;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.saved-ad-footer {
  position: relative;
  background: #2d2d35;
  padding: 0.85rem 0.75rem 2.5rem;
  color: #fff;
}

.saved-ad-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
}

.saved-ad-stats {
  color: #b8b8c4;
  font-size: 0.72rem;
  line-height: 1.35;
}

.saved-bookmark-btn {
  position: absolute;
  bottom: 0.65rem;
  right: 0.65rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.mt-25 {
  margin-top: 0.25rem;
}

/* Reported / Followed tables */
::v-deep .table-head-muted th {
  font-size: 0.72rem;
  letter-spacing: 0.04rem;
  color: #6e6b7b;
  background-color: #f8f8fa !important;
  border-bottom: 1px solid #ebe9f1;
}

::v-deep .reported-table td,
::v-deep .followed-table td {
  vertical-align: middle;
  font-size: 0.9rem;
  color: #5e5873;
}

::v-deep .reported-row td,
::v-deep .followed-row td {
  border-color: #f0f0f5;
}

.rounded-sm {
  border-radius: 6px !important;
}

/* Content preferences */
.pref-toolbar {
  gap: 0.5rem;
}

.pref-action-btn {
  color: #6e6b7b;
  border-color: #d8d6de;
  background: #fff;
  font-weight: 500;
}

.pref-action-btn:hover {
  background: #f8f8fa;
  color: #4b465c;
}

.pref-grid {
  margin-top: 0.25rem;
}

.pref-card {
  border: 1px solid #e8e8ef;
  border-radius: 10px;
  padding: 1rem 0.5rem 0.85rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  background: #fff;
}

.pref-card:hover {
  border-color: #c9c8d1;
}

.pref-card--selected {
  background: #0b1f39 !important;
  border-color: #0b1f39 !important;
  color: #fff;
}

.pref-card--selected .pref-label {
  color: #fff;
}

.pref-card--selected .pref-icon {
  color: #fff;
}

.pref-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.pref-icon {
  color: #5e5873;
}

.pref-check {
  position: absolute;
  top: -6px;
  right: -10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #28c76f;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.pref-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b465c;
  line-height: 1.25;
}
</style>

<style lang="scss">
/* Non-scoped so theme/Bootstrap-Vue cannot override .nav-pills .nav-link.active on this page */
.user-profile-activity-page .activity-tabs-wrap ul.nav.nav-pills .nav-item .nav-link.active {
  background-color: #041122 !important;
  border-color: #041122 !important;
  color: #fff !important;
}

.user-profile-activity-page .stat-card-clicks {
  background-color: #6c72e0 !important;
  box-shadow: 0 12px 32px rgba(108, 114, 224, 0.48) !important;
}
</style>
